import {
  parseJSONFromSelector,
  extractLocations,
  haversine,
  fetchInventory,
  toggle,
  geocodeForMarket,
  isWarehouseLocation,
  isCountryAllowed,
  escapeHtml,
  getAllowedGids,
  readCCState,
  debounce,
  hasLocalPickup,
  isCcStore,
} from '@cc/utils';
import { ensureCoords, getCoords, subscribe as subscribeGeo } from '@cc/geolocation';

if (!customElements.get('cc-store-availability')) {
  customElements.define(
    'cc-store-availability',
    class CCStoreAvailability extends HTMLElement {
      static LOW_STOCK_THRESHOLD = 3;
      static CLOSE_DURATION_MS = 300;
      // Live search while typing: results refresh once typing settles, so the
      // customer never has to press Enter. Shorter queries are too vague to
      // geocode, so they wait for more input.
      static LIVE_SEARCH_DELAY_MS = 350;
      static LIVE_SEARCH_MIN_LENGTH = 3;
      static RESULT_STATES = ['loadingState', 'errorState', 'resultsList', 'warehouseOnlyState', 'noStockState', 'emptyState'];

      constructor() {
        super();
        this.i18n = {};
        this.selectedVariantId = null;
        this.selectedVariantTitle = null;
        this.inventoryData = null;
        this._inventoryFetchPromise = null;
        this.abortController = null;
        this.lastQuery = null;
        this._unsubscribeGeo = null;
        this._debouncedLiveSearch = null;
        this._searchToken = 0;
      }

      get refs() {
        if (this._refs) return this._refs;
        this._refs = {};
        this.querySelectorAll('[ref]').forEach((el) => {
          this._refs[el.getAttribute('ref')] = el;
        });
        return this._refs;
      }

      connectedCallback() {
        this.abortController = new AbortController();
        this.i18n = this.parseJSON('[data-cc-store-availability-i18n]') || {};
        this.radiusKm = Number(this.i18n.radius_km) || 0;
        this.warehouseGid = this.i18n.warehouse_gid || null;
        this.allStores = this.parseJSON('[data-cc-store-availability-stores]') || [];

        this.hasOnlyDefaultVariant = this.dataset.hasOnlyDefaultVariant === 'true';
        if (this.hasOnlyDefaultVariant) {
          const defaultVariantId = Number(this.dataset.defaultVariantId);
          if (defaultVariantId) {
            this.selectedVariantId = defaultVariantId;
            this.selectedVariantTitle = '';
          }
        }

        this.checkCountryAndInit();
      }

      async checkCountryAndInit() {
        const selectorEl = document.querySelector('cc-store-selector [data-cc-store-selector-data]');
        if (selectorEl) {
          try {
            const selectorData = JSON.parse(selectorEl.textContent);
            const allowedCountries = (selectorData?.settings?.allowed_countries || '')
              .split(',')
              .map((c) => c.trim().toUpperCase())
              .filter(Boolean);

            const allowed = await isCountryAllowed(allowedCountries, this.abortController.signal);
            if (!allowed) return;
          } catch {
            // Network error or aborted — proceed with init
          }
        }

        document.querySelectorAll('[data-cc-store-availability-open]').forEach((btn) => {
          btn.classList.remove('hidden');
        });
        this.bindEvents();
        this.subscribeToGeo();

        // Warm the inventory cache in the background so the drawer opens
        // without a visible loading delay when the user clicks Check Availability.
        this._fetchInventory().then((data) => { this.inventoryData = data; }).catch(() => {});
      }

      disconnectedCallback() {
        this._unsubscribeVariantChange?.();
        this.abortController?.abort();
        this._unsubscribeGeo?.();
        this._unsubscribeGeo = null;
        // A pending live search would otherwise fire against a detached node.
        this._debouncedLiveSearch?.cancel();
      }

      parseJSON(selector) {
        return parseJSONFromSelector(this, selector);
      }

      bindEvents() {
        const signal = this.abortController.signal;

        this.refs.closeButton?.addEventListener('click', () => this.close(), { signal });
        this.refs.backdrop?.addEventListener('click', () => this.close(), { signal });

        // Close modal when user clicks a store-selector link inside results
        this.addEventListener('click', (event) => {
          const link = event.target.closest('[data-cc-store-selector-open]');
          if (!link) return;
          event.preventDefault();
          this.close();
        }, { signal });
        this.refs.changeButton?.addEventListener('click', () => this.showStep('select'), { signal });
        this.refs.geoButton?.addEventListener('click', () => this.handleGeo(), { signal });

        // External triggers (proxy buttons outside the component)
        document.querySelectorAll('[data-cc-store-availability-open]').forEach((btn) => {
          btn.addEventListener('click', () => this.open(), { signal });
        });

        // CTA button
        this.refs.ctaButton?.addEventListener('click', () => this.handleCta(), { signal });

        // Search input enter
        this.refs.searchInput?.addEventListener(
          'keydown',
          (e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              this.handleCta();
            }
          },
          { signal },
        );

        // Search input live search — results follow the query without the
        // customer having to submit it.
        this._debouncedLiveSearch = debounce(
          () => this.handleCta({ live: true }),
          CCStoreAvailability.LIVE_SEARCH_DELAY_MS,
        );
        this.refs.searchInput?.addEventListener(
          'input',
          () => {
            const value = this.refs.searchInput.value.trim();
            const onResults = this._step === 'results';
            // New text makes any search still in flight stale — bump the token
            // now, not when the debounced search starts, or a slow answer for
            // "Guimar" can land while the field already reads "Guimarã".
            ++this._searchToken;
            // 1–3 digits is a postal code still being typed — nothing to
            // geocode yet, so don't search on "481" on the way to "4810".
            if (value.length < CCStoreAvailability.LIVE_SEARCH_MIN_LENGTH || /^\d{1,3}$/.test(value)) {
              this._debouncedLiveSearch.cancel();
              // Nothing to search yet, but the list below still carries the
              // previous query's km — redraw it without a query.
              if (onResults) this._renderWithoutQuery();
              return;
            }
            // Until the debounced search runs, the list below belongs to the
            // previous query; its km must not sit under the new text.
            if (onResults) this.showResultState('loadingState');
            this._debouncedLiveSearch();
          },
          { signal },
        );

        // Size buttons
        this.querySelectorAll('.cc-size-btn').forEach((btn) => {
          btn.addEventListener(
            'click',
            () => {
              this.selectSize(btn.dataset.variantId, btn.textContent.trim());
            },
            { signal },
          );
        });

        // Escape
        document.addEventListener(
          'keydown',
          (e) => {
            if (e.key === 'Escape') this.close();
          },
          { signal },
        );

        // Listen for variant changes from PDP
        this._unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const variant = event.data?.variant;
          if (variant) {
            this.selectedVariantId = variant.id;
            this.selectedVariantTitle = variant.title || variant.public_title || '';
            this.inventoryData = null;
            this._inventoryFetchPromise = null;
            this.highlightSize(variant.id);
          } else {
            this.selectedVariantId = null;
            this.selectedVariantTitle = null;
            this.inventoryData = null;
            this._inventoryFetchPromise = null;
          }
        });
      }

      // --- Visibility helpers ---

      showEl(el) { toggle(el, true); }
      hideEl(el) { toggle(el, false); }

      // --- Panel open/close ---

      open() {
        const sheet = this.refs.sheet;
        if (!sheet) return;

        this.showEl(sheet);

        // Always show select step so the user can enter a location;
        // preserve results step only when data was already loaded.
        if (!this.inventoryData) {
          this.showStep('select');
        }

        // Pre-select size if variant already chosen on PDP
        if (this.selectedVariantId) {
          this.highlightSize(this.selectedVariantId);
          this.updateCtaState();
        }

        requestAnimationFrame(() => {
          sheet.classList.remove('translate-y-full', 'md:translate-x-full');
          sheet.classList.add('translate-y-0', 'md:translate-x-0');
          this.refs.backdrop?.classList.add('opacity-100', 'pointer-events-auto');
          this.refs.backdrop?.classList.remove('opacity-0', 'pointer-events-none');
        });

        document.body.classList.add('overflow-hidden');
      }

      close() {
        const sheet = this.refs.sheet;
        if (!sheet) return;

        sheet.classList.add('translate-y-full', 'md:translate-x-full');
        sheet.classList.remove('translate-y-0', 'md:translate-x-0');
        this.refs.backdrop?.classList.remove('opacity-100', 'pointer-events-auto');
        this.refs.backdrop?.classList.add('opacity-0', 'pointer-events-none');

        document.body.classList.remove('overflow-hidden');

        setTimeout(() => {
          this.hideEl(sheet);
        }, CCStoreAvailability.CLOSE_DURATION_MS);
      }

      // --- Steps ---

      showStep(step) {
        this._step = step;
        const isSelect = step === 'select';
        if (isSelect) {
          this.showEl(this.refs.stepSelect);
          this.hideEl(this.refs.stepResults);
          this.hideEl(this.refs.selectedSizeBar);
        } else {
          this.hideEl(this.refs.stepSelect);
          this.showEl(this.refs.stepResults);
        }

        // The location search sits between the steps on both of them; only its
        // gap to the size buttons (step 1) or the size bar (step 2) changes.
        this.refs.locationSearch?.classList.toggle('mt-11', isSelect);
        this.refs.locationSearch?.classList.toggle('mt-4', !isSelect);

        // Show CTA on select step, hide all result bars
        if (this.refs.ctaBar) {
          if (isSelect) {
            this.refs.ctaBar.classList.remove('hidden');
            if (this.refs.ctaButton)
              this.refs.ctaButton.textContent = this.i18n.cta_label;
            this.updateCtaState();
          } else {
            this.refs.ctaBar.classList.add('hidden');
          }
        }

        // Hide result-step bars when going back to select
        if (isSelect) {
          if (this.refs.downloadAppBar) this.refs.downloadAppBar.classList.add('hidden');
        }

        if (!isSelect) {
          if (this.hasOnlyDefaultVariant) {
            this.hideEl(this.refs.selectedSizeBar);
          } else {
            this.showEl(this.refs.selectedSizeBar);
            if (this.refs.selectedSizeLabel) {
              this.refs.selectedSizeLabel.textContent = ' ' + (this.selectedVariantTitle || '');
            }
          }
        }
      }

      updateCtaState() {
        const btn = this.refs.ctaButton;
        if (!btn) return;
        const disabled = !this.selectedVariantId;
        btn.classList.toggle('cc-cta-disabled', disabled);
      }

      // --- Size error feedback ---

      showSizeError() {
        this.refs.sizeError?.classList.remove('hidden');
      }

      hideSizeError() {
        this.refs.sizeError?.classList.add('hidden');
      }

      showGeoError() {
        this.refs.geoError?.classList.remove('hidden');
      }

      hideGeoError() {
        this.refs.geoError?.classList.add('hidden');
      }

      // --- Size selection ---

      selectSize(variantId, title) {
        this.selectedVariantId = Number(variantId);
        this.selectedVariantTitle = title;
        this.inventoryData = null;
        this._inventoryFetchPromise = null;
        this.highlightSize(variantId);
        this.updateCtaState();
        this.hideSizeError();
      }

      highlightSize(variantId) {
        this.querySelectorAll('.cc-size-btn').forEach((btn) => {
          const isActive = String(btn.dataset.variantId) === String(variantId);
          btn.classList.toggle('cc-size-btn--active', isActive);
        });
      }

      // --- Geo icon states: default / active / loading ---

      setGeoState(state) {
        const { geoIconDefault, geoIconActive, geoButtonLoader } = this.refs;

        if (geoIconDefault) geoIconDefault.classList.toggle('hidden', state !== 'default');
        if (geoIconActive) geoIconActive.classList.toggle('hidden', state !== 'active');
        if (geoButtonLoader) geoButtonLoader.classList.toggle('hidden', state !== 'loading');
      }

      subscribeToGeo() {
        this._unsubscribeGeo = subscribeGeo(({ lat, lng }) => {
          const hasCoords = lat !== null && lng !== null;
          this.setGeoState(hasCoords ? 'active' : 'default');
          // Re-render current results with new distances.
          if (this.inventoryData) {
            this.renderResults(this.lastQuery, lat, lng);
          }
        });
      }

      // --- CTA handler ---

      /**
       * @param {{live?: boolean}} [options] - `live` when triggered by typing
       *   (debounced) rather than Enter or the CTA button. A live run stays
       *   silent when no size is picked yet — the customer hasn't submitted.
       */
      async handleCta({ live = false } = {}) {
        if (!this.selectedVariantId) {
          if (!live) this.showSizeError();
          return;
        }

        // An explicit submit supersedes a pending live search.
        if (!live) this._debouncedLiveSearch?.cancel();

        // Each run invalidates any still-in-flight previous one, so a slow
        // geocode from an earlier keystroke can't resolve last and clobber
        // the results of a newer query.
        const searchToken = ++this._searchToken;

        this.lastQuery = this.refs.searchInput?.value?.trim() || null;
        this.showStep('results');
        // The list on screen belongs to the previous query — its km would be
        // wrong for the whole geocode, retries on a 5xx included.
        this.showResultState('loadingState');

        let lat = null;
        let lng = null;

        // Always geocode the query — the store availability modal treats the query
        // as a location reference point for sorting results by distance.
        // Text-only filtering is only a fallback when geocoding fails.
        // Scoped to the visitor's market so short queries ("Braga", a postal
        // code) don't resolve to — or fail on — a location in another country.
        if (this.lastQuery) {
          const geocoded = await geocodeForMarket(this.lastQuery, {
            countryCode: this.i18n.current_country,
            countryName: this.i18n.current_country_name,
            stores: this.allStores,
          });
          if (geocoded) {
            lat = geocoded.lat;
            lng = geocoded.lng;
          }
        }

        if (searchToken !== this._searchToken) return;

        // Fall back to browser geolocation if geocoding failed or no query
        if (lat === null || lng === null) {
          const coords = getCoords();
          lat = coords?.lat ?? null;
          lng = coords?.lng ?? null;
        }

        await this.fetchAndRender(this.lastQuery, lat, lng);
      }

      /**
       * Redraw the results as for an empty query (in stock first, or by
       * distance from the visitor's own location if shared), dropping any
       * geocode still in flight for the text that was just erased.
       */
      _renderWithoutQuery() {
        ++this._searchToken;
        this.lastQuery = null;
        if (!this.inventoryData) return;
        const coords = getCoords();
        this.renderResults(null, coords?.lat ?? null, coords?.lng ?? null);
      }

      // --- Geolocation button ---

      async handleGeo() {
        if (!this.selectedVariantId) {
          this.showSizeError();
          return;
        }
        this.hideGeoError();
        this.setGeoState('loading');
        const coords = await ensureCoords({ prompt: true });

        if (!coords) {
          // Permission denied — stay on the select step so the customer can
          // still search manually, and show a short explanation.
          this.setGeoState('default');
          this.showGeoError();
          return;
        }

        if (this.refs.searchInput) {
          this.refs.searchInput.value = this.i18n.geo_current_location || '';
        }

        // The visitor's location replaces whatever was typed — drop a pending
        // or in-flight text search so it can't overwrite these results.
        this._debouncedLiveSearch?.cancel();
        ++this._searchToken;

        this.showStep('results');
        await this.fetchAndRender(null, coords.lat, coords.lng);
        this.setGeoState('active');
      }

      // --- Fetch + Render ---

      async fetchAndRender(query, lat, lng, searchToken = this._searchToken) {
        this.showResultState('loadingState');

        try {
          const data = await this._fetchInventory();
          // A newer search, or new text in the field, came in while stock loaded.
          if (searchToken !== this._searchToken) return;
          if (!data) {
            this.showResultState('errorState');
            return;
          }

          this.inventoryData = data;
          this.renderResults(query, lat, lng);
        } catch (err) {
          console.error('[cc-store-availability] Fetch failed:', err);
          this.showResultState('errorState');
        }
      }

      async _fetchInventory() {
        if (this.inventoryData) return this.inventoryData;
        if (!this._inventoryFetchPromise) {
          this._inventoryFetchPromise = fetchInventory(
            this.dataset.productId,
            this.abortController.signal,
          ).then((data) => {
            // fetchInventory() resolves null on a failed request instead of
            // throwing — drop that promise too, or every later search would
            // reuse the failure and show the error until a page reload.
            if (!data) this._inventoryFetchPromise = null;
            return data;
          }).catch((err) => {
            // Allow a retry on the next call if the fetch failed.
            this._inventoryFetchPromise = null;
            throw err;
          });
        }
        return this._inventoryFetchPromise;
      }

      showResultState(stateName, { hasOnlineStock = false } = {}) {
        for (const s of CCStoreAvailability.RESULT_STATES) {
          s === stateName ? this.showEl(this.refs[s]) : this.hideEl(this.refs[s]);
        }

        // "Also available online" banner shows alongside resultsList when warehouse stock exists
        // toggle(this.refs.alsoOnlineBanner, stateName === 'resultsList' && hasOnlineStock);

        const showDownloadApp = stateName === 'noStockState';
        if (this.refs.downloadAppBar) {
          this.refs.downloadAppBar.classList.toggle('hidden', !showDownloadApp);
        }
      }

      /**
       * Enrich inventory locations with metaobject store data (zip, lat/lng, city, address).
       * Matches on location GID. `isMarketStore` marks a location that is a store
       * in this market, whatever its status; `ccActive` additionally requires the
       * store's status to be on and its location to have local pickup on (both
       * only decide Click & Collect, not whether the store's stock is shown).
       */
      enrichLocations(locations) {
        if (!this.allStores?.length) {
          return locations.map((loc) => ({ ...loc, isMarketStore: false, ccActive: false }));
        }

        return locations.map((loc) => {
          const meta = this.allStores.find((s) => s.location_id === loc.id);
          if (!meta) return { ...loc, isMarketStore: false, ccActive: false };

          return {
            ...loc,
            isMarketStore: true,
            ccActive: isCcStore(meta) && hasLocalPickup(loc),
            name: meta.name || loc.name,
            city: meta.city || loc.city,
            zip: meta.zip || loc.zip,
            address: [meta.address1, meta.city, meta.zip].filter(Boolean).join(', ') || loc.address,
            lat: meta.latitude != null ? Number(meta.latitude) : loc.lat,
            lng: meta.longitude != null ? Number(meta.longitude) : loc.lng,
          };
        });
      }

      renderResults(query, lat, lng) {
        if (!this.inventoryData) return;

        let locations = extractLocations(this.inventoryData, this.selectedVariantId);

        if (!locations) {
          this.showResultState('errorState');
          return;
        }

        if (locations.length === 0) {
          this.showResultState('noStockState');
          return;
        }

        // Enrich with metaobject data for accurate search and geo filtering
        locations = this.enrichLocations(locations);

        // Only show locations that match a store in this market, whatever its
        // status (always keep the warehouse so online-stock logic still works)
        const isWarehouse = (loc) => isWarehouseLocation(loc, this.warehouseGid);
        locations = locations.filter((loc) => loc.isMarketStore || isWarehouse(loc));

        // Separate stores from warehouses (match by isWarehouse flag or warehouse GID)
        const storeLocations = locations.filter((loc) => !isWarehouse(loc));
        const warehouseLocations = locations.filter(isWarehouse);
        const storeStock = storeLocations.reduce((sum, loc) => sum + (loc.available || 0), 0);
        const warehouseStock = warehouseLocations.reduce((sum, loc) => sum + (loc.available || 0), 0);

        // Stores within the customer's active fulfillment cluster (selected
        // store + nearby stores). Used to decide whether a card's C&C message
        // can be the short "available" form or needs to invite a store change.
        const allowedGids = getAllowedGids();
        const preferredStoreGid = readCCState()?.selectedStoreGid || null;

        // No stock anywhere
        if (storeStock === 0 && warehouseStock === 0) {
          this.showResultState('noStockState');
          return;
        }

        // No stock in stores, but available online (warehouse)
        if (storeStock === 0 && warehouseStock > 0) {
          this.showResultState('warehouseOnlyState');
          return;
        }

        // Show store cards (exclude warehouses from card list)
        let filteredStores = storeLocations;

        // Default order when there is no reference point: in stock first, then by name.
        const sortByAvailability = (stores) =>
          stores.sort((a, b) => {
            if (a.available > 0 && b.available <= 0) return -1;
            if (a.available <= 0 && b.available > 0) return 1;
            return (a.name || '').localeCompare(b.name || '');
          });

        // Sort by distance when coordinates are available (geocoded or geolocation)
        if (lat != null && lng != null) {
          filteredStores = filteredStores
            .map((loc) => ({
              ...loc,
              distance: loc.lat && loc.lng ? haversine(lat, lng, loc.lat, loc.lng) : Infinity,
            }))
            // In-stock stores first, each group nearest first — a sold-out store
            // next door must not rank above the nearest store that has the size.
            .sort((a, b) => (a.available > 0 ? 0 : 1) - (b.available > 0 ? 0 : 1) || a.distance - b.distance);

          // Radius filtering disabled for now
          // if (this.radiusKm > 0) {
          //   filteredStores = filteredStores.filter((loc) => loc.distance <= this.radiusKm);
          // }
        } else if (query) {
          // No reference point — geocoding failed and the visitor hasn't
          // shared their location. Fall back to text matching.
          const q = query.toLowerCase();
          const matchesQuery = (loc) =>
            [loc.name, loc.city, loc.zip, loc.address].filter(Boolean).some((v) => v.toLowerCase().includes(q));

          // Never drop the non-matching stores: a query that matches nothing
          // (or only sold-out stores) must still list everywhere the product
          // is in stock — the sold-out states are only for when no store has
          // it at all. Text matches stay pinned on top so the searched area
          // still reads first.
          filteredStores = [
            ...sortByAvailability(filteredStores.filter(matchesQuery)),
            ...sortByAvailability(filteredStores.filter((loc) => !matchesQuery(loc))),
          ];
        } else {
          sortByAvailability(filteredStores);
        }

        // Render cards
        const container = this.refs.resultsList;
        if (!container) return;
        container.innerHTML = '';

        const template = this.querySelector('[ref="storeCardTemplate"]');
        if (!template) return;

        for (const store of filteredStores) {
          const card = template.content.cloneNode(true);

          card.querySelector('[data-store-name]').textContent = store.name || '';
          card.querySelector('[data-store-address]').textContent = store.address || '';

          // Distance
          const distEl = card.querySelector('[data-store-distance]');
          if (distEl) {
            if (store.distance != null && store.distance !== Infinity) {
              distEl.textContent = `${store.distance.toFixed(1)} km`;
            } else {
              distEl.remove();
            }
          }

          // Status badge
          const badge = card.querySelector('[data-store-badge]');
          if (store.available <= 0) {
            badge.textContent = this.i18n.sold_out;
            badge.classList.add('cc-badge--sold-out');
          } else if (store.available <= CCStoreAvailability.LOW_STOCK_THRESHOLD) {
            badge.textContent = this.i18n.last_units;
            badge.classList.add('cc-badge--last-units');
          } else {
            badge.textContent = this.i18n.in_stock;
            badge.classList.add('cc-badge--in-stock');
          }

          // C&C message — only for stores with Click & Collect on. A store with
          // its status off can't be picked in the Store Selector, and one
          // without local pickup can't fulfil a pickup order.
          const ccMsg = card.querySelector('[data-store-cc-message]');
          if (store.available > 0 && store.ccActive && ccMsg) {
            const isEligibleStore = Boolean(allowedGids?.includes(store.id));

            if (preferredStoreGid && isEligibleStore) {
              // Already the customer's preferred (or a nearby eligible) store —
              // no need to prompt them to select it again.
              ccMsg.textContent = this.i18n.cc_message_available;
            } else {
              const escapedName = escapeHtml(store.name || '');
              ccMsg.innerHTML =
                `${escapeHtml(this.i18n.cc_message_line1)} ` +
                `<a href="#" class="font-medium underline" data-cc-store-selector-open data-store-name="${escapedName}">${escapeHtml(this.i18n.cc_message_link)}</a> ` +
                `${escapeHtml(this.i18n.cc_message_line2)}`;
            }
            ccMsg.classList.remove('hidden');
          }

          container.appendChild(card);
        }

        this.showResultState('resultsList', { hasOnlineStock: warehouseStock > 0 });
      }

      // Called by product-info.js
      update(variant) {
        if (variant?.id) {
          this.selectedVariantId = variant.id;
          this.selectedVariantTitle = variant.title || variant.public_title || '';
        } else {
          this.selectedVariantId = null;
          this.selectedVariantTitle = null;
        }
        this.inventoryData = null;
        this._inventoryFetchPromise = null;
      }
    },
  );
}
